import React, { useState, useEffect } from "react";
import { getData, postData, putData,deleteData } from "../../../../services/Fetchnodeservices";

// ===================== THEME =====================
const getT = (isDark) => ({
  bg: isDark ? "#0c1018" : "#f0f4ff",
  surface: isDark ? "#141824" : "#ffffff",
  surfaceAlt: isDark ? "#1c2133" : "#f4f7ff",
  border: isDark ? "rgba(255,255,255,0.07)" : "#e2e8f5",
  text: isDark ? "#f1f5f9" : "#0f172a",
  textMuted: isDark ? "#64748b" : "#94a3b8",
  textSub: isDark ? "#94a3b8" : "#64748b",
  shadow: isDark ? "0 4px 28px rgba(0,0,0,0.45)" : "0 4px 28px rgba(99,102,241,0.10)",
  shadowSm: isDark ? "0 2px 10px rgba(0,0,0,0.35)" : "0 2px 10px rgba(99,102,241,0.07)",
  accent: "#6c63ff",
  success: "#22c55e",
  warning: "#f59e0b",
  danger: "#ef4444",
  info: "#3b82f6",
});

// ===================== ICONS =====================
const SearchIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);
const MapPinIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);
const PlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);
const EditIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);
const CloseIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
const ProviderIcon = ({ source }) => {
  const label = source === "OpenStreetMap" ? "O" : "?";
  const color = source === "OpenStreetMap" ? "#7c3aed" : "#64748b";
  return (
    <span style={{
      width: "24px", height: "24px", borderRadius: "6px",
      background: `${color}18`, color, fontSize: "11px", fontWeight: 700,
      display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
    }}>{label}</span>
  );
};

// ===================== MAIN COMPONENT =====================
const Locations = ({ isDark = true }) => {
  const t = getT(isDark);

  // ---- State ----
  const [zones, setZones] = useState([]);
  const [loadingZones, setLoadingZones] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  // City search (powered by OpenStreetMap via backend)
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchProvider, setSearchProvider] = useState(null);
  const searchTimerRef = React.useRef(null);
  const searchControllerRef = React.useRef(null);

  // Selected result from search (for adding as zone)
  const [selectedSearchResult, setSelectedSearchResult] = useState(null);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("add");
  const [editingZone, setEditingZone] = useState(null);
  const [formData, setFormData] = useState({
    name: "", city: "", polygon: [], zone_type: "allowed", status: "1"
  });

  // City filter dropdown
  const [availableCities, setAvailableCities] = useState([]);
  const [loadingCities, setLoadingCities] = useState(true);
  const [selectedCity, setSelectedCity] = useState("");

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  // ===================== DATA LOADING =====================

  // Load cities from DB
  useEffect(() => {
    let cancelled = false;
    async function loadCities() {
      try {
        setLoadingCities(true);
        const res = await getData("admin/settings/regions-cities");
        if (!cancelled && res && res.status && Array.isArray(res.data)) {
          const cities = res.data.map(c => c.city).filter(Boolean);
          setAvailableCities(cities);
        }
      } catch (e) {
        console.warn("Failed to load cities:", e);
      } finally {
        if (!cancelled) setLoadingCities(false);
      }
    }
    loadCities();
    return () => { cancelled = true; };
  }, []);

  // Load zones (with optional city filter)
  const loadZones = React.useCallback(async (city) => {
    try {
      setLoadingZones(true);
      const params = new URLSearchParams();
      if (city) params.set("city", city);
      const res = await getData(`admin/settings/regions?${params.toString()}`);
      if (res && res.status && Array.isArray(res.data)) {
        setZones(res.data);
      } else {
        setZones([]);
      }
    } catch (e) {
      console.warn("Zone load failed:", e);
      setZones([]);
    } finally {
      setLoadingZones(false);
    }
  }, []);

  useEffect(() => {
    loadZones(selectedCity);
  }, [selectedCity, loadZones]);

  // ===================== OPEnStreetMap SEARCH =====================

  // Search for locations — powered by OpenStreetMap Nominatim via backend
  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);

    if (!searchQuery || searchQuery.trim().length < 2) {
      setSearchResults([]);
      setSearching(false);
      setSearchProvider(null);
      return;
    }

    setSearching(true);
    setSearchProvider(null);

    searchTimerRef.current = setTimeout(async () => {
      if (searchControllerRef.current) {
        searchControllerRef.current.abort();
      }
      const controller = new AbortController();
      searchControllerRef.current = controller;

      try {
        const params = new URLSearchParams();
        params.set("q", searchQuery.trim());
        params.set("limit", "12");
        const res = await getData(`admin/settings/regions/search?${params.toString()}`);
        if (controller.signal.aborted) return;

        if (res && res.status && Array.isArray(res.data)) {
          setSearchResults(res.data);
          setSearchProvider(res.provider || null);
        } else {
          setSearchResults([]);
        }
      } catch (e) {
        if (controller.signal.aborted) return;
        console.warn("Location search failed:", e);
        setSearchResults([]);
      } finally {
        if (!controller.signal.aborted) {
          setSearching(false);
        }
      }
    }, 350);

    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, [searchQuery]);

  // ===================== CRUD OPERATIONS =====================

  const handleAddFromSearch = (result) => {
    // When admin selects a search result, open a form to create a zone
    const cityFromResult = result.address?.city || "";
    const nameFromResult = (result.display_name || result.name || "").split(",")[0]?.trim() || result.name;

    setModalMode("add");
    setEditingZone(null);
    setSelectedSearchResult(result);
    setFormData({
      name: nameFromResult,
      city: cityFromResult,
      polygon: result.polygon || [],
      zone_type: "allowed",
      status: "1",
    });
    setShowModal(true);
  };

  const handleCreateZone = async () => {
    if (!formData.name || !formData.city) {
      showToast("Zone name and city are required", "error");
      return;
    }
    if (!formData.polygon || formData.polygon.length < 3) {
      showToast("Please select a valid location with polygon data", "error");
      return;
    }

    try {
      setSaving(true);
      const res = await postData("admin/settings/regions", formData);
      if (res && res.status) {
        showToast("Zone created successfully");
        setShowModal(false);
        setSelectedSearchResult(null);
        setSearchQuery("");
        setSearchResults([]);
        setFormData({ name: "", city: "", polygon: [], zone_type: "allowed", status: "1" });
        loadZones(selectedCity);
      } else {
        showToast(res.message || "Failed to create zone", "error");
      }
    } catch (e) {
      showToast("Error creating zone: " + e.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateZone = async () => {
    if (!editingZone) return;
    try {
      setSaving(true);
      const res = await putData(`admin/settings/regions/${editingZone.id}`, formData);
      if (res && res.status) {
        showToast("Zone updated successfully");
        setShowModal(false);
        setEditingZone(null);
        setFormData({ name: "", city: "", polygon: [], zone_type: "allowed", status: "1" });
        loadZones(selectedCity);
      } else {
        showToast(res.message || "Failed to update zone", "error");
      }
    } catch (e) {
      showToast("Error updating zone: " + e.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteZone = async (id) => {
    if (!window.confirm("Delete this zone? This action cannot be undone.")) return;
    try {
      const res = await deleteData(`admin/settings/regions/${id}`);
      if (res && res.status) {
        showToast("Zone deleted");
        loadZones(selectedCity);
      } else {
        showToast(res.message || "Failed to delete", "error");
      }
    } catch (e) {
      showToast("Error deleting zone", "error");
    }
  };

  const handleToggleStatus = async (zone) => {
    try {
      const res = await postData(`admin/settings/regions/${zone.id}/toggle-status`, {});
      if (res && res.status) {
        showToast("Status updated");
        loadZones(selectedCity);
      }
    } catch (e) {
      showToast("Error toggling status", "error");
    }
  };

  const handleEditZone = (zone) => {
    setModalMode("edit");
    setEditingZone(zone);
    const city = zone.city || "";
    setFormData({
      name: zone.name?.replace(/^\[[^\]]+\]\s*/, "") || "",
      city: city,
      polygon: zone.polygon || [],
      zone_type: zone.zone_type || "allowed",
      status: String(zone.status ?? "1"),
    });
    setShowModal(true);
  };

  // ===================== HELPERS =====================

  function inferCity(name) {
    const value = String(name || "").trim();
    const tagged = value.match(/^\[([^\]]+)\]\s*/);
    if (tagged) return tagged[1].trim();
    return (value.split(",")[0] || "").trim() || null;
  }

  function polygonPointCount(polygon) {
    if (!polygon || !Array.isArray(polygon)) return 0;
    return polygon.length;
  }

  const filteredZones = React.useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return zones;
    return zones.filter(z => {
      const name = (z.name || "").toLowerCase();
      const city = (z.city || inferCity(z.name) || "").toLowerCase();
      return name.includes(q) || city.includes(q);
    });
  }, [zones, searchQuery]);

  // ===================== RENDER =====================

  return (
    <div style={{
      minHeight: "100vh", background: t.bg, fontFamily: "'DM Sans', sans-serif",
      padding: "24px",
    }}>
      <div style={{ maxWidth: "1400px", margin: "0 auto" }}>

        {/* Header */}
        <div style={{ marginBottom: "28px" }}>
          <h1 style={{
            fontSize: "28px", fontWeight: 700,
            background: "linear-gradient(135deg, #6c63ff, #a855f7)",
            WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", margin: 0,
          }}>📍 Region Management</h1>
          <p style={{ fontSize: "13px", color: t.textMuted, marginTop: "6px" }}>
            Search any city/state across India with OpenStreetMap, then create delivery zones
          </p>
        </div>

        {/* Provider Status Cards */}
        <div style={{ display: "flex", gap: "12px", marginBottom: "24px", flexWrap: "wrap" }}>
          <div style={{
            background: t.surface, border: `1px solid ${t.border}`, borderRadius: "12px",
            padding: "14px 18px", flex: 1, minWidth: "200px", display: "flex", alignItems: "center", gap: "10px",
          }}>
            <div style={{
              width: "28px", height: "28px", borderRadius: "8px", background: "#7c3aed18", color: "#7c3aed",
              display: "flex", alignItems: "center", justifyContent: "center", fontSize: "12px", fontWeight: 700,
            }}>O</div>
            <div>
              <div style={{ fontSize: "13px", fontWeight: 600, color: t.text }}>OpenStreetMap</div>
              <div style={{ fontSize: "11px", color: t.textMuted }}>
                Primary provider — free &amp; open
              </div>
            </div>
          </div>
          <div style={{
            background: t.surface, border: `1px solid ${t.border}`, borderRadius: "12px",
            padding: "14px 18px", flex: 1, minWidth: "200px", display: "flex", alignItems: "center", gap: "10px",
          }}>
            <div style={{
              width: "28px", height: "28px", borderRadius: "8px", background: `${t.success}18`, color: t.success,
              display: "flex", alignItems: "center", justifyContent: "center", fontSize: "12px", fontWeight: 700,
            }}>{zones.length}</div>
            <div>
              <div style={{ fontSize: "13px", fontWeight: 600, color: t.text }}>Active Zones</div>
              <div style={{ fontSize: "11px", color: t.textMuted }}>
                {zones.filter(z => String(z.status) === "1").length} active of {zones.length} total
              </div>
            </div>
          </div>
        </div>

        {/* Search Bar + City Filter */}
        <div style={{
          background: t.surface, border: `1px solid ${t.border}`, borderRadius: "14px",
          padding: "18px 20px", marginBottom: "20px",
          boxShadow: `0 2px 8px ${t.accent}08`,
        }}>
          <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
            {/* Search Input */}
            <div style={{ flex: 1, minWidth: "280px", position: "relative" }}>
              <span style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: t.textMuted }}>
                <SearchIcon />
              </span>
              <input
                style={{
                  width: "100%", padding: "11px 12px 11px 36px", background: t.surfaceAlt,
                  border: `1px solid ${searchResults.length > 0 ? t.accent : t.border}`,
                  borderRadius: "10px", fontSize: "14px", color: t.text, outline: "none",
                  boxShadow: searchResults.length > 0 ? `0 0 0 3px ${t.accent}15` : "none",
                  transition: "all 0.2s",
                }}
                placeholder="Search city, state, locality — powered by OpenStreetMap..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoFocus
              />
              {searchQuery && (
                <button onClick={() => { setSearchQuery(""); setSearchResults([]); setSearchProvider(null); }}
                  style={{
                    position: "absolute", right: "10px", top: "50%", transform: "translateY(-50%)",
                    background: "none", border: "none", cursor: "pointer", color: t.textMuted, padding: "4px",
                  }}>
                  <CloseIcon />
                </button>
              )}
              {searching && (
                <span style={{ position: "absolute", right: "36px", top: "50%", transform: "translateY(-50%)", fontSize: "11px", color: t.textMuted }}>
                  Searching...
                </span>
              )}
            </div>

            {/* City Filter Dropdown */}
            <select
              style={{
                padding: "11px 14px", background: t.surfaceAlt, border: `1px solid ${t.border}`,
                borderRadius: "10px", fontSize: "13px", color: t.text, cursor: "pointer", minWidth: "180px",
              }}
              value={selectedCity}
              onChange={(e) => setSelectedCity(e.target.value)}
            >
              <option value="">All Cities</option>
              {availableCities.map(city => (
                <option key={city} value={city}>{city}</option>
              ))}
            </select>

            {/* Add Zone button */}
            <button
              onClick={() => {
                if (!selectedSearchResult && searchResults.length === 0) {
                  showToast("Search for a location first, then select it to add a zone", "error");
                  return;
                }
                if (searchResults.length > 0) {
                  handleAddFromSearch(searchResults[0]);
                }
              }}
              style={{
                display: "flex", alignItems: "center", gap: "8px",
                padding: "11px 18px", background: "linear-gradient(135deg, #6c63ff, #a855f7)",
                color: "#fff", border: "none", borderRadius: "10px", cursor: "pointer",
                fontSize: "13px", fontWeight: 600, whiteSpace: "nowrap",
              }}
            >
              <PlusIcon /> Add Zone
            </button>
          </div>

          {/* Search Results Dropdown */}
          {searchResults.length > 0 && (
            <div style={{
              marginTop: "10px", background: t.surfaceAlt, border: `1px solid ${t.border}`,
              borderRadius: "10px", overflow: "hidden", maxHeight: "340px", overflowY: "auto",
              boxShadow: `0 8px 24px rgba(0,0,0,0.15)`,
            }}>
              <div style={{
                padding: "8px 14px", fontSize: "11px", color: t.textMuted,
                borderBottom: `1px solid ${t.border}`, display: "flex", justifyContent: "space-between",
              }}>
                <span>
                  🟣 OpenStreetMap Nominatim results for "{searchQuery}"
                </span>
                <span>{searchResults.length} found</span>
              </div>
              {searchResults.map((item, idx) => {
                const city = item.address?.city || inferCity(item.display_name || item.name);
                const hasPolygon = item.polygon && Array.isArray(item.polygon) && item.polygon.length >= 3;
                const displayName = item.display_name || item.display || item.name;
                return (
                  <div
                    key={item.place_id || `${item.source || "r"}-${idx}`}
                    onClick={() => handleAddFromSearch(item)}
                    style={{
                      padding: "10px 14px", cursor: "pointer", display: "flex",
                      alignItems: "flex-start", gap: "10px",
                      borderBottom: idx < searchResults.length - 1 ? `1px solid ${t.border}` : "none",
                      transition: "background 0.15s",
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = t.accent + "10"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                  >
                    <div style={{ marginTop: "2px" }}>
                      <ProviderIcon source={item.source || item.category} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: "13px", fontWeight: 600, color: t.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {displayName.split(",")[0]}
                      </div>
                      <div style={{ fontSize: "11px", color: t.textMuted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {displayName}
                      </div>
                      <div style={{ display: "flex", gap: "8px", marginTop: "4px", flexWrap: "wrap" }}>
                        {city && (
                          <span style={{
                            fontSize: "10px", padding: "2px 8px", borderRadius: "6px",
                            background: `${t.accent}15`, color: t.accent, fontWeight: 500,
                          }}>{city}</span>
                        )}
                        {hasPolygon && (
                          <span style={{
                            fontSize: "10px", padding: "2px 8px", borderRadius: "6px",
                            background: `${t.success}15`, color: t.success, fontWeight: 500,
                          }}>polygon ({item.polygon.length} pts)</span>
                        )}
                        {item.type && (
                          <span style={{
                            fontSize: "10px", padding: "2px 8px", borderRadius: "6px",
                            background: `${t.info}15`, color: t.info, fontWeight: 500,
                          }}>{item.type}</span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleAddFromSearch(item); }}
                      style={{
                        padding: "5px 12px", background: `${t.accent}15`, color: t.accent,
                        border: `1px solid ${t.accent}30`, borderRadius: "8px", cursor: "pointer",
                        fontSize: "11px", fontWeight: 600, whiteSpace: "nowrap",
                      }}
                    >
                      Add Zone
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* No results message */}
          {searchQuery.length >= 2 && !searching && searchResults.length === 0 && (
            <div style={{
              marginTop: "10px", padding: "14px", background: `${t.warning}08`,
              border: `1px solid ${t.warning}30`, borderRadius: "10px", fontSize: "13px", color: t.textMuted,
            }}>
              No results found for "{searchQuery}". Try a different city, state, or locality name.
            </div>
          )}
        </div>

        {/* Zones Table */}
        <div style={{
          background: t.surface, border: `1px solid ${t.border}`, borderRadius: "14px", overflow: "hidden",
          boxShadow: `0 2px 8px ${t.accent}08`,
        }}>
          <div style={{
            padding: "16px 20px", borderBottom: `1px solid ${t.border}`, display: "flex",
            justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px",
            background: t.surfaceAlt,
          }}>
            <h3 style={{ margin: 0, fontSize: "16px", fontWeight: 700, color: t.text }}>
              Delivery Zones {selectedCity ? `in ${selectedCity}` : "(All Cities)"}
            </h3>
            <span style={{ fontSize: "12px", color: t.textMuted }}>
              {filteredZones.length} zone{filteredZones.length !== 1 ? "s" : ""}
            </span>
          </div>

          {loadingZones ? (
            <div style={{ padding: "40px", textAlign: "center", color: t.textMuted }}>
              Loading zones...
            </div>
          ) : filteredZones.length === 0 ? (
            <div style={{
              padding: "40px", textAlign: "center", color: t.textMuted,
            }}>
              <div style={{ fontSize: "40px", marginBottom: "12px" }}>📍</div>
              <p style={{ margin: "0 0 8px", fontSize: "14px", fontWeight: 600, color: t.text }}>
                No zones yet
              </p>
              <p style={{ margin: 0, fontSize: "13px" }}>
                Search for a city/locality above and click "Add Zone" to create your first delivery zone
              </p>
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={{ padding: "12px 16px", textAlign: "left", fontSize: "12px", fontWeight: 700, color: t.accent, borderBottom: `1px solid ${t.border}`, background: t.surfaceAlt }}>Zone Name</th>
                    <th style={{ padding: "12px 16px", textAlign: "left", fontSize: "12px", fontWeight: 700, color: t.accent, borderBottom: `1px solid ${t.border}`, background: t.surfaceAlt }}>City</th>
                    <th style={{ padding: "12px 16px", textAlign: "left", fontSize: "12px", fontWeight: 700, color: t.accent, borderBottom: `1px solid ${t.border}`, background: t.surfaceAlt }}>Type</th>
                    <th style={{ padding: "12px 16px", textAlign: "left", fontSize: "12px", fontWeight: 700, color: t.accent, borderBottom: `1px solid ${t.border}`, background: t.surfaceAlt }}>Boundary</th>
                    <th style={{ padding: "12px 16px", textAlign: "left", fontSize: "12px", fontWeight: 700, color: t.accent, borderBottom: `1px solid ${t.border}`, background: t.surfaceAlt }}>Status</th>
                    <th style={{ padding: "12px 16px", textAlign: "left", fontSize: "12px", fontWeight: 700, color: t.accent, borderBottom: `1px solid ${t.border}`, background: t.surfaceAlt }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredZones.map(zone => {
                    const city = zone.city || inferCity(zone.name);
                    const polygonPts = zone.polygon && Array.isArray(zone.polygon) ? zone.polygon.length : 0;
                    const isActive = String(zone.status) === "1";
                    return (
                      <tr key={zone.id} style={{ borderBottom: `1px solid ${t.border}` }}>
                        <td style={{ padding: "12px 16px", fontSize: "13px", color: t.textSub }}>
                          <strong style={{ color: t.text }}>{zone.name?.replace(/^\[[^\]]+\]\s*/, "")}</strong>
                        </td>
                        <td style={{ padding: "12px 16px", fontSize: "13px", color: t.textSub }}>
                          {city || <span style={{ color: t.textMuted }}>—</span>}
                        </td>
                        <td style={{ padding: "12px 16px", fontSize: "13px" }}>
                          <span style={{
                            padding: "3px 10px", borderRadius: "8px", fontSize: "11px", fontWeight: 600,
                            background: zone.zone_type === "blacklisted" ? `${t.danger}15` : `${t.success}15`,
                            color: zone.zone_type === "blacklisted" ? t.danger : t.success,
                          }}>
                            {zone.zone_type === "blacklisted" ? "🚫 Blocked" : "✅ Allowed"}
                          </span>
                        </td>
                        <td style={{ padding: "12px 16px", fontSize: "12px", color: t.textMuted }}>
                          {polygonPts > 0 ? `${polygonPts} points` : <span style={{ color: `${t.warning}` }}>no polygon</span>}
                        </td>
                        <td style={{ padding: "12px 16px", fontSize: "13px" }}>
                          <span style={{
                            padding: "3px 10px", borderRadius: "8px", fontSize: "11px", fontWeight: 600,
                            background: isActive ? `${t.success}15` : `${t.textMuted}15`,
                            color: isActive ? t.success : t.textMuted,
                          }}>
                            {isActive ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td style={{ padding: "12px 16px" }}>
                          <div style={{ display: "flex", gap: "6px" }}>
                            <button onClick={() => handleEditZone(zone)} title="Edit" style={{
                              background: "none", border: `1px solid ${t.border}`, cursor: "pointer",
                              padding: "5px 8px", borderRadius: "6px", color: t.accent, display: "inline-flex",
                            }}><EditIcon /></button>
                            <button onClick={() => handleToggleStatus(zone)} title={isActive ? "Deactivate" : "Activate"} style={{
                              background: "none", border: `1px solid ${t.border}`, cursor: "pointer",
                              padding: "5px 10px", borderRadius: "6px", color: isActive ? t.warning : t.success,
                              fontSize: "11px", fontWeight: 600,
                            }}>
                              {isActive ? "Deactivate" : "Activate"}
                            </button>
                            <button onClick={() => handleDeleteZone(zone.id)} title="Delete" style={{
                              background: "none", border: `1px solid ${t.border}`, cursor: "pointer",
                              padding: "5px 10px", borderRadius: "6px", color: t.danger, fontSize: "11px", fontWeight: 600,
                            }}>Delete</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* ===================== MODAL ===================== */}
        {showModal && (
          <div style={{
            position: "fixed", inset: 0, zIndex: 200, background: "rgba(0,0,0,0.6)",
            backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center",
          }}
            onClick={() => { setShowModal(false); setEditingZone(null); setSelectedSearchResult(null); }}
          >
            <div onClick={(e) => e.stopPropagation()} style={{
              background: t.surface, border: `1px solid ${t.border}`, borderRadius: "16px",
              width: "520px", maxWidth: "95vw", maxHeight: "85vh", overflow: "auto",
            }}>
              <div style={{
                padding: "18px 22px", borderBottom: `1px solid ${t.border}`, display: "flex",
                justifyContent: "space-between", alignItems: "center",
              }}>
                <h3 style={{ margin: 0, fontSize: "17px", fontWeight: 700, color: t.text }}>
                  {modalMode === "add" ? "Add Delivery Zone" : "Edit Delivery Zone"}
                </h3>
                <button onClick={() => { setShowModal(false); setEditingZone(null); setSelectedSearchResult(null); }}
                  style={{ background: "none", border: "none", cursor: "pointer", color: t.textMuted, padding: "4px" }}>
                  <CloseIcon />
                </button>
              </div>

              <div style={{ padding: "22px" }}>
                {/* Show selected search result info */}
                {selectedSearchResult && modalMode === "add" && (
                  <div style={{
                    padding: "10px 14px", background: `${t.accent}08`, border: `1px solid ${t.accent}20`,
                    borderRadius: "10px", marginBottom: "18px", fontSize: "12px", color: t.textMuted,
                  }}>
                    📍 Selected: <strong style={{ color: t.text }}>{selectedSearchResult.display_name || selectedSearchResult.name}</strong>
                    {selectedSearchResult.polygon && selectedSearchResult.polygon.length >= 3 && (
                      <span style={{ marginLeft: "8px", color: t.success }}>✓ Polygon loaded ({selectedSearchResult.polygon.length} pts)</span>
                    )}
                  </div>
                )}

                <div style={{ marginBottom: "14px" }}>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: 600, color: t.textMuted, marginBottom: "5px" }}>
                    Zone Name *
                  </label>
                  <input
                    style={{
                      width: "100%", padding: "10px 14px", background: t.surfaceAlt,
                      border: `1px solid ${t.border}`, borderRadius: "10px", fontSize: "13px",
                      color: t.text, outline: "none",
                    }}
                    placeholder="e.g. Gole Ka Mandir Delivery Zone"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </div>

                <div style={{ marginBottom: "14px" }}>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: 600, color: t.textMuted, marginBottom: "5px" }}>
                    City *
                  </label>
                  <input
                    style={{
                      width: "100%", padding: "10px 14px", background: t.surfaceAlt,
                      border: `1px solid ${t.border}`, borderRadius: "10px", fontSize: "13px",
                      color: t.text, outline: "none",
                    }}
                    placeholder="e.g. Gwalior"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  />
                </div>

                <div style={{ marginBottom: "14px" }}>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: 600, color: t.textMuted, marginBottom: "5px" }}>
                    Zone Type
                  </label>
                  <select
                    style={{
                      width: "100%", padding: "10px 14px", background: t.surfaceAlt,
                      border: `1px solid ${t.border}`, borderRadius: "10px", fontSize: "13px",
                      color: t.text, cursor: "pointer",
                    }}
                    value={formData.zone_type}
                    onChange={(e) => setFormData({ ...formData, zone_type: e.target.value })}
                  >
                    <option value="allowed">Allowed (Delivery Zone)</option>
                    <option value="blacklisted">Blacklisted (No Delivery)</option>
                  </select>
                </div>

                <div style={{ marginBottom: "14px" }}>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: 600, color: t.textMuted, marginBottom: "5px" }}>
                    Status
                  </label>
                  <select
                    style={{
                      width: "100%", padding: "10px 14px", background: t.surfaceAlt,
                      border: `1px solid ${t.border}`, borderRadius: "10px", fontSize: "13px",
                      color: t.text, cursor: "pointer",
                    }}
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  >
                    <option value="1">Active</option>
                    <option value="0">Inactive</option>
                  </select>
                </div>

                {/* Polygon info */}
                <div style={{
                  padding: "10px 14px", background: `${formData.polygon?.length >= 3 ? t.success : t.warning}08`,
                  border: `1px solid ${formData.polygon?.length >= 3 ? t.success : t.warning}30`,
                  borderRadius: "10px", fontSize: "12px", color: t.textMuted, marginBottom: "14px",
                }}>
                  {formData.polygon?.length >= 3
                    ? `✅ Polygon boundary: ${formData.polygon.length} coordinate points`
                    : formData.polygon?.length > 0
                      ? `⚠️ Polygon has only ${formData.polygon.length} points (minimum 3 required)`
                      : "⚠️ No polygon boundary. Select a location with polygon data from the search results above."
                  }
                </div>

                <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
                  <button onClick={() => { setShowModal(false); setEditingZone(null); setSelectedSearchResult(null); }}
                    style={{
                      padding: "10px 20px", background: t.surfaceAlt, border: `1px solid ${t.border}`,
                      borderRadius: "10px", color: t.textSub, cursor: "pointer", fontSize: "13px", fontWeight: 600,
                    }}>
                    Cancel
                  </button>
                  <button
                    onClick={modalMode === "add" ? handleCreateZone : handleUpdateZone}
                    disabled={saving || !formData.name || !formData.city || (formData.polygon?.length || 0) < 3}
                    style={{
                      padding: "10px 24px", background: "linear-gradient(135deg, #6c63ff, #a855f7)",
                      color: "#fff", border: "none", borderRadius: "10px", cursor: saving ? "not-allowed" : "pointer",
                      fontSize: "13px", fontWeight: 600, opacity: (saving || !formData.name || !formData.city) ? 0.6 : 1,
                    }}
                  >
                    {saving ? "Saving..." : modalMode === "add" ? "Create Zone" : "Update Zone"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Toast */}
        {toast && (
          <div style={{
            position: "fixed", bottom: "24px", right: "24px", zIndex: 1000,
            background: t.surface, border: `1px solid ${toast.type === "error" ? t.danger : t.success}`,
            borderRadius: "12px", padding: "12px 20px", display: "flex", alignItems: "center", gap: "10px",
            boxShadow: t.shadow,
          }}>
            <div style={{
              width: "8px", height: "8px", borderRadius: "50%",
              background: toast.type === "error" ? t.danger : t.success,
            }} />
            <span style={{ fontSize: "13px", color: toast.type === "error" ? t.danger : t.success }}>
              {toast.message}
            </span>
            <button onClick={() => setToast(null)} style={{ background: "none", border: "none", cursor: "pointer", color: t.textMuted, padding: "2px" }}>
              <CloseIcon />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Locations;
